/**
 * InstanceDetail — workspace landing page (multi-bot).
 *
 * Tabs:
 *   - Chat     — StructuredChat for claude_code/codex; existing /chat
 *                page covers OpenClaw (this page links there for openclaw).
 *   - Logs     — TODO: hook up agent log SSE (next iteration).
 *   - Config   — link to existing InstanceConfig page.
 *   - Events   — TODO: surface agent_events table per session.
 *
 * Remote-attach mode hides Start/Stop controls (the session isn't ours
 * to lifecycle) and surfaces the share host as a status pill.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import StructuredChat from '../components/StructuredChat';
import TerminalSession from '../components/TerminalSession';
import ConnectClaudeModal from '../components/ConnectClaudeModal';
import { API_URL } from '../services/api';
import { useApiHeaders } from '../hooks/useApiHeaders';
import { botTypeLabel } from '../lib/botTypes';

interface InstanceInfo {
  instance_id: string;
  instance_name: string;
  agent_id: string;
  bot_type: string;
  running_status: string;
  workspace_dir?: string | null;
  workspace_share_host?: string | null;
  has_workspace_share_url?: boolean;
  active_session_id?: string | null;
}

type Tab = 'chat' | 'logs' | 'config' | 'events' | 'terminal';

export default function InstanceDetailPage() {
  const { id: instanceId } = useParams<{ id: string }>();
  const { getHeaders } = useApiHeaders();
  const [instance, setInstance] = useState<InstanceInfo | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('chat');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [anthropicConnected, setAnthropicConnected] = useState<boolean | null>(null);
  const [showConnectModal, setShowConnectModal] = useState(false);

  // ── Load instance metadata + session ────────────────────────────────────
  useEffect(() => {
    if (!instanceId) return;
    let cancelled = false;
    (async () => {
      try {
        const headers = await getHeaders();
        const res = await fetch(`${API_URL}/api/v1/instances/${instanceId}`, { headers });
        if (!res.ok) throw new Error(`Instance fetch failed: ${res.status}`);
        const data = await res.json() as InstanceInfo;
        if (cancelled) return;
        setInstance(data);

        // Resolve active session (regenerate if absent).
        const sRes = await fetch(`${API_URL}/api/v1/instances/${instanceId}/sessions/active`, { headers });
        if (sRes.ok) {
          const sData = await sRes.json() as { active_session_id?: string | null };
          if (sData.active_session_id) {
            setSessionId(sData.active_session_id);
          } else {
            const newRes = await fetch(`${API_URL}/api/v1/instances/${instanceId}/sessions/new`, {
              method: 'POST', headers,
            });
            if (newRes.ok) {
              const newData = await newRes.json() as { active_session_id: string };
              setSessionId(newData.active_session_id);
            }
          }
        }

        // For remote-attach, fetch the decrypted share URL just-in-time.
        if (data.has_workspace_share_url) {
          const urlRes = await fetch(`${API_URL}/api/v1/instances/${instanceId}/share-url`, {
            headers: await getHeaders(),
          });
          if (urlRes.ok) {
            const urlData = await urlRes.json() as { workspace_share_url?: string };
            if (urlData.workspace_share_url) setShareUrl(urlData.workspace_share_url);
          }
        }
      } catch (e: any) {
        if (!cancelled) setError(String(e?.message ?? e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [instanceId, getHeaders]);

  // ── Check Anthropic credential status for claude_code instances ─────────
  const refreshCredentialStatus = useCallback(async () => {
    if (!instanceId || !instance || instance.bot_type !== 'claude_code') return;
    try {
      const headers = await getHeaders();
      const res = await fetch(`${API_URL}/api/v1/credentials/${instanceId}`, { headers });
      if (!res.ok) return;
      const data = await res.json() as {
        credentials?: Record<string, { is_configured?: boolean }>;
      };
      setAnthropicConnected(!!data.credentials?.anthropic?.is_configured);
    } catch {
      /* non-fatal */
    }
  }, [instanceId, instance, getHeaders]);

  useEffect(() => { void refreshCredentialStatus(); }, [refreshCredentialStatus]);

  const handleNewSession = useCallback(async (): Promise<string | null> => {
    if (!instanceId) return null;
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/api/v1/instances/${instanceId}/sessions/new`, {
      method: 'POST', headers,
    });
    if (!res.ok) return null;
    const data = await res.json() as { active_session_id: string };
    setSessionId(data.active_session_id);
    return data.active_session_id;
  }, [instanceId, getHeaders]);

  const handleSwitchSession = useCallback(async (sid: string): Promise<void> => {
    if (!instanceId) return;
    const headers = await getHeaders();
    const res = await fetch(`${API_URL}/api/v1/instances/${instanceId}/sessions/switch`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sid }),
    });
    if (res.ok) setSessionId(sid);
  }, [instanceId, getHeaders]);

  const isRemote = !!instance?.has_workspace_share_url;
  const isMultiBot = instance && instance.bot_type !== 'openclaw';

  const headerTitle = useMemo(() => {
    if (!instance) return 'Workspace';
    return instance.instance_name;
  }, [instance]);

  const headerSubtitle = useMemo(() => {
    if (!instance) return '';
    return `${botTypeLabel(instance.bot_type)} · ${instance.running_status}`;
  }, [instance]);

  return (
    <Layout title={headerTitle} subtitle={headerSubtitle}>
      <div className="px-6 py-4">
        {error && (
          <div className="mb-4 p-3 rounded-xl border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-400">
            {error}
          </div>
        )}

        {/* Workspace target pill */}
        {instance && (
          <div className="mb-4 flex items-center space-x-2 text-xs">
            {instance.workspace_dir && (
              <span className="px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 font-mono">
                📁 {instance.workspace_dir}
              </span>
            )}
            {isRemote && (
              <span className="px-2 py-0.5 rounded-md bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400">
                🔗 Remote share{instance.workspace_share_host ? ` · ${instance.workspace_share_host}` : ''}
              </span>
            )}
            {!isMultiBot && (
              <span className="text-slate-400">OpenClaw — use the Chat tab below or open the dedicated chat page.</span>
            )}
          </div>
        )}

        {/* Connect Anthropic banner — only for claude_code instances
            without a saved credential. Local-mode workspaces only;
            remote-attach uses its own URL-based auth. */}
        {instance && instance.bot_type === 'claude_code' && !isRemote && anthropicConnected === false && (
          <div className="mb-4 flex items-center justify-between p-3 rounded-xl border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-900/20">
            <div className="text-sm text-amber-700 dark:text-amber-400">
              <span className="font-medium">Connect Anthropic to use this workspace.</span>
              <span className="ml-1 text-amber-600 dark:text-amber-500">Claude Code needs an authentication token before it can run.</span>
            </div>
            <button
              type="button"
              onClick={() => setShowConnectModal(true)}
              className="ml-3 px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-600 hover:bg-amber-700 text-white cursor-pointer"
            >
              Connect now
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-slate-200 dark:border-slate-800 mb-4">
          {(['chat', 'logs', 'config', 'events', ...(isMultiBot && !isRemote ? (['terminal'] as Tab[]) : [])] as Tab[]).map(t => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-medium border-b-2 cursor-pointer ${
                tab === t
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
              }`}
            >
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden" style={{ minHeight: 480 }}>
          {loading || !instance ? (
            <div className="p-6 text-sm text-slate-400 dark:text-slate-600">Loading…</div>
          ) : tab === 'chat' ? (
            isMultiBot ? (
              <div className="h-[600px]">
                <StructuredChat
                  instanceId={instance.instance_id}
                  agentId={instance.agent_id}
                  sessionId={sessionId}
                  shareUrl={shareUrl}
                  onNewSession={handleNewSession}
                  onSwitchSession={handleSwitchSession}
                />
              </div>
            ) : (
              <div className="p-6">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  OpenClaw chat lives on the existing Chat page.
                </p>
                <Link
                  to="/chat"
                  className="inline-block mt-3 text-sm text-primary-600 dark:text-primary-400 hover:underline"
                >
                  Open Chat →
                </Link>
              </div>
            )
          ) : tab === 'config' ? (
            <div className="p-6">
              <Link
                to={`/instances/${instance.instance_id}/config`}
                className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
              >
                Open full configuration →
              </Link>
            </div>
          ) : tab === 'terminal' ? (
            isMultiBot && !isRemote ? (
              <TerminalSession
                instanceId={instance.instance_id}
                instanceName={instance.instance_name}
                agentId={instance.agent_id}
                isActive={tab === 'terminal'}
              />
            ) : (
              <div className="p-6 text-sm text-slate-400 dark:text-slate-600">
                Terminal mode is only available for local claude_code / codex workspaces.
              </div>
            )
          ) : (
            <div className="p-6 text-sm text-slate-400 dark:text-slate-600">
              {tab === 'logs' && 'Logs view ships with the next iteration.'}
              {tab === 'events' && 'Events view ships with the next iteration.'}
            </div>
          )}
        </div>
      </div>

      {showConnectModal && instance && (
        <ConnectClaudeModal
          instanceId={instance.instance_id}
          agentId={instance.agent_id}
          onClose={() => setShowConnectModal(false)}
          onConnected={() => {
            setShowConnectModal(false);
            void refreshCredentialStatus();
          }}
        />
      )}
    </Layout>
  );
}
